import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { Form, Header } from 'semantic-ui-react';
import { DatesRangeInput } from 'semantic-ui-calendar-react-17';
import PropTypes from 'prop-types';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';
import 'moment/locale/pt-br';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { reportActions } from '../../actions/report.actions';
import './activity-dashboard.css';

const DATE_FORMAT = 'DD/MM/YYYY';
const FILTER_FORMAT = 'YYYY-MM-DD';

const TEAL = '#1fa774';
const AMBER = '#e0822f';
const SLATE = '#5a6b71';
const LINE = '#dbe5e8';

const STATUS_LABELS = { CREATED: 'Criada', APPROVED: 'Aprovada', REJECTED: 'Rejeitada', EXPIRED: 'Expirada' };
const STATUS_COLORS = { CREATED: '#2185d0', APPROVED: TEAL, REJECTED: '#db2828', EXPIRED: '#9aa7ab' };
const TYPE_LABELS = {
  SCHEDULED: 'Agendado',
  ADDITIONAL: 'Adicional',
  FULLDAY: 'Dia Inteiro',
  START: 'Início Antecipado',
  UNLOCK: 'Início Jornada',
};

// Compact, human duration ("242h 40m"); the readout font does the rest.
function fmtHM(seconds) {
  return moment.duration(seconds || 0, 'seconds').format('h[h] mm[m]', { trim: false });
}

const axisTick = { fontSize: 11, fill: SLATE };

// Tooltip for the duration charts: shows each series as h:mm rather than the raw hours decimal.
function DurationTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="agx-tip">
      <div className="agx-tip-label">{label}</div>
      {payload.map((p) => (
        <div className="agx-tip-row" key={p.dataKey}>
          <span className="agx-dot" style={{ background: p.color }} />
          {p.name}
          <span className="agx-tip-val">{moment.duration(p.value, 'hours').format('h[h] mm[m]', { trim: false })}</span>
        </div>
      ))}
    </div>
  );
}

function CountTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="agx-tip">
      <div className="agx-tip-label">{label || payload[0].name}</div>
      <div className="agx-tip-row">
        <span className="agx-dot" style={{ background: payload[0].payload.fill || payload[0].color }} />
        {payload[0].name}
        <span className="agx-tip-val">{payload[0].value}</span>
      </div>
    </div>
  );
}

function ChartKey({ items }) {
  return (
    <div className="agx-key">
      {items.map((i) => (
        <span className="agx-key-item" key={i.label}>
          <span className="agx-dot" style={{ background: i.color }} />
          {i.label}
        </span>
      ))}
    </div>
  );
}

function secondsToHours(seconds) {
  return Math.round(((seconds || 0) / 3600) * 100) / 100;
}

class ActivityDashboardScreen extends Component {
  constructor(props) {
    super(props);
    this.state = { search: '', period: '' };
  }

  static get propTypes() {
    return { dashboard: PropTypes.any, loading: PropTypes.bool };
  }

  componentDidMount() {
    const end = moment().format(DATE_FORMAT);
    const start = moment().subtract(6, 'days').format(DATE_FORMAT);
    this.setState({ period: `${start} - ${end}` }, () => this.fetchData());
  }

  handlePeriodChange(period) {
    this.setState({ period }, () => this.fetchData());
  }

  handleSearch(value) {
    clearTimeout(this._t);
    this.setState({ search: value });
    this._t = setTimeout(() => this.fetchData(), 500);
  }

  fetchData() {
    const { dispatch } = this.props;
    const { search, period } = this.state;
    var [startDate, endDate] = (period || '').split(' - ');
    if (startDate) startDate = moment(startDate, DATE_FORMAT).format(FILTER_FORMAT);
    if (endDate) endDate = moment(endDate, DATE_FORMAT).format(FILTER_FORMAT);
    dispatch(reportActions.fetchActivityDashboard({ username: search, startDate, endDate }));
  }

  render() {
    const { dashboard, loading } = this.props;
    const { period } = this.state;

    const totals = dashboard?.totals || {};
    const requests = dashboard?.requests || {};

    const open = totals.openSeconds || 0;
    const locked = totals.lockedSeconds || 0;
    const denom = open + locked;
    const activePct = denom > 0 ? Math.round((open / denom) * 100) : 0;

    const perDayData = (dashboard?.perDay || []).map((d) => ({
      date: moment(d.date).format('DD/MM'),
      Ativo: secondsToHours(d.openSeconds),
      Bloqueado: secondsToHours(d.lockedSeconds),
    }));

    const topUsersData = (dashboard?.topUsers || []).map((u) => ({
      name: u.Name || u.Username,
      Ativo: secondsToHours(u.openSeconds),
      Bloqueado: secondsToHours(u.lockedSeconds),
    }));

    const statusData = (requests.byStatus || []).map((s) => ({
      name: STATUS_LABELS[s.statusLabel] || s.statusLabel,
      value: s.count,
      fill: STATUS_COLORS[s.statusLabel] || SLATE,
    }));

    const typeData = (requests.byType || []).map((t) => ({
      name: TYPE_LABELS[t.typeLabel] || t.typeLabel,
      Solicitações: t.count,
    }));

    return (
      <div className="agx-dash">
        <Header>
          <h1>Analytics Sessões</h1>
        </Header>

        <div className="agx-toolbar">
          {loading && <span className="agx-loading">atualizando…</span>}
          <Form>
            <Form.Group style={{ margin: 0 }}>
              <DatesRangeInput
                name="period"
                localization="pt-br"
                placeholder="Todo o período"
                value={period}
                iconPosition="left"
                dateFormat={DATE_FORMAT}
                label="Período"
                closable
                clearable
                onChange={(e, data) => this.handlePeriodChange(data.value)}
              />
              <Form.Input icon="search" label="Usuário" placeholder="username" onChange={(e, data) => this.handleSearch(data.value)} />
            </Form.Group>
          </Form>
        </div>

        <div className="agx-console">
          <div className="agx-kpis">
            <div className="agx-kpi agx-kpi--hero agx-kpi--active">
              <span className="agx-kpi-label">Tempo ativo</span>
              <span className="agx-kpi-value">{fmtHM(open)}</span>
            </div>
            <div className="agx-kpi agx-kpi--hero agx-kpi--locked">
              <span className="agx-kpi-label">Tempo bloqueado</span>
              <span className="agx-kpi-value">{fmtHM(locked)}</span>
            </div>

            <div className="agx-kpi-sep" />

            <div className="agx-kpi agx-kpi--mini">
              <span className="agx-kpi-label">Usuários</span>
              <span className="agx-kpi-value">{totals.users || 0}</span>
            </div>
            <div className="agx-kpi agx-kpi--mini">
              <span className="agx-kpi-label">Sessões ativas</span>
              <span className="agx-kpi-value">{totals.activeSessions || 0}</span>
            </div>
            <div className="agx-kpi agx-kpi--mini">
              <span className="agx-kpi-label">Solicitações</span>
              <span className="agx-kpi-value">{requests.total || 0}</span>
            </div>
            <div className="agx-kpi agx-kpi--mini">
              <span className="agx-kpi-label">Horas aprovadas</span>
              <span className="agx-kpi-value">{fmtHM((requests.approvedMinutes || 0) * 60)}</span>
            </div>
          </div>

          <div className="agx-gauge">
            <div className="agx-gauge-track">
              <div className="agx-gauge-fill agx-gauge-active" style={{ width: `${activePct}%` }} />
              <div className="agx-gauge-fill agx-gauge-locked" style={{ width: `${100 - activePct}%` }} />
            </div>
            <div className="agx-gauge-legend">
              <span><span className="agx-pct">{activePct}%</span> do tempo ativo</span>
              <span><span className="agx-pct">{100 - activePct}%</span> bloqueado</span>
            </div>
          </div>
        </div>

        <div className="agx-panel">
          <div className="agx-panel-head">
            <span className="agx-panel-title">Atividade por dia</span>
            <ChartKey items={[{ label: 'Ativo', color: TEAL }, { label: 'Bloqueado', color: AMBER }]} />
          </div>
          {perDayData.length === 0 ? (
            <div className="agx-empty">Sem atividade no período selecionado.</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={perDayData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }} barCategoryGap="28%">
                <CartesianGrid stroke={LINE} vertical={false} />
                <XAxis dataKey="date" tick={axisTick} axisLine={{ stroke: LINE }} tickLine={false} />
                <YAxis tick={axisTick} axisLine={false} tickLine={false} width={36} />
                <Tooltip cursor={{ fill: 'rgba(4,34,46,.04)' }} content={<DurationTooltip />} />
                <Bar dataKey="Ativo" stackId="a" fill={TEAL} maxBarSize={46} />
                <Bar dataKey="Bloqueado" stackId="a" fill={AMBER} maxBarSize={46} radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="agx-grid2">
          <div className="agx-panel">
            <div className="agx-panel-head">
              <span className="agx-panel-title">Top usuários por tempo ativo</span>
            </div>
            {topUsersData.length === 0 ? (
              <div className="agx-empty">Sem dados de usuários.</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={topUsersData} layout="vertical" margin={{ top: 0, right: 12, left: 8, bottom: 0 }} barCategoryGap="26%">
                  <CartesianGrid stroke={LINE} horizontal={false} />
                  <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={axisTick} axisLine={false} tickLine={false} width={130} />
                  <Tooltip cursor={{ fill: 'rgba(4,34,46,.04)' }} content={<DurationTooltip />} />
                  <Bar dataKey="Ativo" stackId="u" fill={TEAL} maxBarSize={22} />
                  <Bar dataKey="Bloqueado" stackId="u" fill={AMBER} maxBarSize={22} radius={[0, 5, 5, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
            <ChartKey items={[{ label: 'Ativo', color: TEAL }, { label: 'Bloqueado', color: AMBER }]} />
          </div>

          <div className="agx-panel">
            <div className="agx-panel-head">
              <span className="agx-panel-title">Solicitações por situação</span>
              <span className="agx-panel-meta">{requests.total || 0} total</span>
            </div>
            {statusData.length === 0 ? (
              <div className="agx-empty">Nenhuma solicitação no período.</div>
            ) : (
              <div className="agx-donut-wrap">
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={70} outerRadius={104} paddingAngle={2} cornerRadius={4} stroke="none">
                      {statusData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CountTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="agx-donut-center">
                  <span className="agx-donut-total">{requests.total || 0}</span>
                  <span className="agx-donut-cap">solicitações</span>
                </div>
              </div>
            )}
            <ChartKey items={statusData.map((s) => ({ label: s.name, color: s.fill }))} />
          </div>
        </div>

        <div className="agx-panel">
          <div className="agx-panel-head">
            <span className="agx-panel-title">Solicitações por tipo</span>
          </div>
          {typeData.length === 0 ? (
            <div className="agx-empty">Nenhuma solicitação no período.</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={typeData} margin={{ top: 4, right: 8, left: -8, bottom: 0 }} barCategoryGap="34%">
                <CartesianGrid stroke={LINE} vertical={false} />
                <XAxis dataKey="name" tick={axisTick} axisLine={{ stroke: LINE }} tickLine={false} />
                <YAxis allowDecimals={false} tick={axisTick} axisLine={false} tickLine={false} width={36} />
                <Tooltip cursor={{ fill: 'rgba(4,34,46,.04)' }} content={<CountTooltip />} />
                <Bar dataKey="Solicitações" fill={'#004867'} maxBarSize={54} radius={[5, 5, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { report } = state;
  return {
    dashboard: report?.activityDashboard || null,
    loading: report?.loading,
  };
}

const connectedActivityDashboardScreen = connect(mapStateToProps)(ActivityDashboardScreen);
export { connectedActivityDashboardScreen as ActivityDashboardScreen };
